import {useEffect, useState, useContext, useRef} from 'react';
import {View, ScrollView, FlatList, TextInput, Animated} from 'react-native';
import {Divider} from 'react-native-paper';
import {useNavigation, useIsFocused} from '@react-navigation/native';
import moment from 'moment';
import _ from 'lodash';

import sy from '~/styles';
import {
  Text,
  ServiceRequestStatusIcon,
  ReplacementTileIcon,
  Pressable,
} from '~/components/controls';
import {DealerHeader} from '~/components/shared';
import {STATUS_DESCRIPTIONS, STATUS_ORDER} from '~/types/statuses';
import {overview as overviewApi, search as searchApi} from '~/api';
import UserContext from '~/components/context/UserContext';
import {useSelector, useDispatch} from '~/lib/hooks';
import Format from '~/lib/format';
import {SERVICE_TYPE_CODES} from '~/types/services';
import {setAppIsLoading, setAppDoneLoading} from '~/actions';
import withProtectedScreen from '~/components/hoc/with-protected-screen';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import SearchIcon from '~/images/md-icons/search/materialicons/24px.svg';
import CarRepairIcon from '~/images/md-icons/car_repair/materialicons/24px.svg';
import ApprovalIcon from '~/images/md-icons/approval/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import BuildIcon from '~/images/md-icons/build_repair/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import CarCrashIcon from '~/images/md-icons/car_crash/materialicons/24px.svg';
import ExpandLessIcon from '~/images/md-icons/expand_less/materialicons/24px.svg';
import ExpandMoreIcon from '~/images/md-icons/expand_more/materialicons/24px.svg';

const MONTH_HEIGHT = 53;
const REQUEST_HEIGHT = 92;

const TYPES = {
  ROB_REQUEST: 'ROB_REQUEST',
  ROB_REPLACEMENT_VEHICLE: 'ROB_REPLACEMENT_VEHICLE',
  ROB_DAMAGE_REQUEST: 'ROB_DAMAGE_REQUEST',
};

const StatusHeader = ({status, count, onBack}) => (
  <View style={[sy['flex-row']]}>
    <Pressable style={[sy['p-4']]} onPress={onBack}>
      <ChevronLeftIcon />
    </Pressable>
    <View
      style={[
        sy['flex-1'],
        sy['py-4'],
        sy['items-center'],
        sy['flex-row'],
        sy['gap-2'],
      ]}>
      <Text style={[sy.mediumPlus, {lineHeight: 24}]}>
        {STATUS_DESCRIPTIONS[status]}
        {count > 0 ? <>&nbsp;&middot;&nbsp;{count}</> : null}
      </Text>
    </View>
    <View style={[sy['p-4']]}>
      <ServiceRequestStatusIcon status={status} />
    </View>
  </View>
);

const StatusCard = ({status, count, onPress}) => (
  <Pressable onPress={onPress} style={[sy['p-4'], sy['bg-white']]}>
    <View style={[sy['flex-row'], sy['justify-between'], sy['items-center']]}>
      <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
        <ServiceRequestStatusIcon status={status} />
        <Text style={sy.mediumBold}>{STATUS_DESCRIPTIONS[status]}</Text>
      </View>
      <Text style={[sy['text-lightgray'], sy.smallRegular]}>
        {Format.number(count, 0, true)}
      </Text>
    </View>
  </Pressable>
);

const Type = ({type, style}) => {
  if (!type) {
    return null;
  }

  let icon;
  switch (type) {
    case SERVICE_TYPE_CODES.Maintenance:
      icon = <CarRepairIcon fill="#4A4A49" style={style} />;
      break;
    case SERVICE_TYPE_CODES.Inspection:
      icon = <ApprovalIcon fill="#4A4A49" style={style} />;
      break;
    case SERVICE_TYPE_CODES.Tires:
      icon = <TireRepairIcon fill="#4A4A49" style={style} />;
      break;
    case 'DamageRequest':
      icon = <CarCrashIcon fill="#4A4A49" style={style} />;
      break;
    case 'ReplacementCar':
      icon = <CarRentalIcon fill="#4A4A49" style={style} />;
      break;
    default:
      icon = <BuildIcon fill="#4A4A49" style={style} />;
      break;
  }

  return icon;
};

const Request = ({request, index}) => {
  const navigation = useNavigation();
  const {
    rob_request_id,
    type,
    status,
    license,
    car,
    dealer,
    datetime,
    replacement,
    icons,
  } = request;
  return (
    <Pressable
      style={[
        sy['flex-row'],
        sy['bg-white'],
        sy['mb-2'],
        sy['py-3'],
        sy['px-4'],
        sy['overflow-hidden'],
        {borderRadius: 8, height: REQUEST_HEIGHT - 8},
      ]}
      onPress={() => {
        if (type === TYPES.ROB_REPLACEMENT_VEHICLE) {
          navigation.navigate('ReplacementVehicle', {rob_request_id});
        } else {
          navigation.navigate('Request', {rob_request_id});
        }
      }}>
      <View
        style={[
          sy['items-center'],
          sy['justify-between'],
          {paddingVertical: 2},
          sy['pr-4'],
          {
            borderRightColor: '#DCDCDC',
            borderRightWidth: 1,
          },
        ]}>
        <Text
          style={[
            sy.mediumLarge,
            sy['text-center'],
            {backgroundColor: '#F2F2F2', borderRadius: 6, width: 40},
          ]}>
          {moment(datetime).format('D')}
        </Text>
        <ServiceRequestStatusIcon status={status} />
      </View>
      <View style={[sy['flex-1'], sy['pl-4']]}>
        <View
          style={[
            sy['flex-row'],
            sy['items-center'],
            sy['gap-2'],
            sy['justify-between'],
          ]}>
          <Text style={[sy.mediumPlus, sy.truncate]}>
            {Format.license(license)}
          </Text>
          <View
            style={[
              sy['flex-row'],
              sy['items-center'],
              sy['gap-2'],
              sy['justify-between'],
            ]}>
            {icons.map((icon) => (
              <Type key={icon} type={icon} style={{height: 18, width: 18}} />
            ))}
            {replacement && (
              <ReplacementTileIcon
                type={replacement.type}
                fill="#4A4A49"
                style={{height: 18, width: 18}}
              />
            )}
          </View>
        </View>
        <View style={[sy['flex-row'], sy['gap-2'], sy['justify-between']]}>
          <View style={[sy['flex-1']]}>
            <Text style={[sy.smallRegular, sy.truncate]}>{car.name}</Text>
            <Text style={[sy.smallRegular, sy.truncate]}>{dealer.name}</Text>
          </View>
        </View>
      </View>
    </Pressable>
  );
};

const Month = ({date}) => (
  <View
    style={[
      sy['p-4'],
      sy['flex-row'],
      sy['justify-between'],
      {backgroundColor: '#F2F2F2', height: MONTH_HEIGHT},
    ]}>
    <Text style={sy.mediumBold}>
      {moment(date, 'YYYY-MM').format('MMMM YYYY')}
    </Text>
  </View>
);

const Grouping = ({header, count, children}) => {
  const [expand, setExpand] = useState(true);

  return (
    <View style={[sy['bg-white'], sy['overflow-hidden'], {borderRadius: 8}]}>
      <Pressable
        style={[
          sy['flex-row'],
          sy['items-center'],
          sy['justify-between'],
          sy['p-4'],
          sy['gap-4'],
          sy['bg-white'],
        ]}
        onPress={() => setExpand(!expand)}>
        <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
          {expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Text style={[sy.mediumPlus, sy.truncate]}>{header}</Text>
        </View>
        <Text style={[sy['text-lightgray'], sy.smallRegular]}>
          {Format.number(count, 0, true)}
        </Text>
      </Pressable>
      <Divider style={sy['bg-gray95']} />
      {expand && <>{children}</>}
    </View>
  );
};

const List = ({overview}) => {
  const navigation = useNavigation();

  const request_count = Object.values(overview[TYPES.ROB_REQUEST] ?? {}).reduce(
    (acc, count) => acc + count,
    0,
  );

  const replacement_count = Object.values(
    overview[TYPES.ROB_REPLACEMENT_VEHICLE] ?? {},
  ).reduce((acc, count) => acc + count, 0);

  const damage_count = Object.values(
    overview[TYPES.ROB_DAMAGE_REQUEST] ?? {},
  ).reduce((acc, count) => acc + count, 0);

  return (
    <ScrollView
      style={[sy['flex-1'], sy['bg-gray95'], sy['p-2']]}
      contentContainerStyle={[sy['gap-2']]}>
      <Grouping header="Werkopdracht" count={request_count}>
        {Object.keys(overview[TYPES.ROB_REQUEST] ?? {})
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status) => (
            <StatusCard
              key={status}
              status={status}
              count={overview[TYPES.ROB_REQUEST][status]}
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {status, type: TYPES.ROB_REQUEST},
                })
              }
            />
          ))}
      </Grouping>
      <Grouping header="Vervangende auto" count={replacement_count}>
        {Object.keys(overview[TYPES.ROB_REPLACEMENT_VEHICLE] ?? {})
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status) => (
            <StatusCard
              key={status}
              status={status}
              count={overview[TYPES.ROB_REPLACEMENT_VEHICLE][status]}
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {
                    status,
                    type: TYPES.ROB_REPLACEMENT_VEHICLE,
                  },
                })
              }
            />
          ))}
      </Grouping>
      <Grouping header="Schadewerkopdracht" count={damage_count}>
        {Object.keys(overview[TYPES.ROB_DAMAGE_REQUEST] ?? {})
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status) => (
            <StatusCard
              key={status}
              status={status}
              count={overview[TYPES.ROB_DAMAGE_REQUEST][status]}
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {
                    status,
                    type: TYPES.ROB_DAMAGE_REQUEST,
                  },
                })
              }
            />
          ))}
      </Grouping>
    </ScrollView>
  );
};

const doSearch = _.debounce(
  async ({prefix, selectedType, selectedStatus}, setSelected, dispatch) => {
    let data;
    if (prefix?.length > 2) {
      data = await searchApi.prefix(prefix);
    } else if (selectedStatus) {
      data = await searchApi.filter(selectedType, selectedStatus);
    }

    if (data) {
      setSelected(data);
    } else {
      setSelected(null);
    }

    if (data) {
      setSelected(data);
    } else {
      setSelected(null);
    }

    dispatch(setAppDoneLoading());
  },
  200,
);

const Search = ({navigation, route}) => {
  const isFocused = useIsFocused();
  const dispatch = useDispatch();

  const selectedType = route.params?.type;
  const selectedStatus = route.params?.status;

  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [prefix, setPrefix] = useState('');

  const {me} = useContext(UserContext);

  const forceRefresh = useSelector((state) => state.serviceRequestUpdate);

  const cancelAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', () => {
      if (isFocused) {
        if (navigation.getParent()?.canGoBack()) {
          navigation.popToTop();
        } else {
          setPrefix('');
          setSelected(null);
        }
      }
    });

    return unsubscribe;
  }, [isFocused, navigation]);

  useEffect(() => {
    if (prefix.length) {
      Animated.timing(cancelAnim, {
        toValue: 100,
        duration: 250,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(cancelAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).start();
    }
  }, [cancelAnim, prefix]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    dispatch(setAppIsLoading());

    return () => {
      dispatch(setAppDoneLoading());
    };
  }, [isFocused, dispatch]);

  useEffect(() => {
    if (selectedStatus) {
      return;
    }

    const fetch = async () => {
      const overview = await overviewApi.retrieve();

      dispatch(setAppDoneLoading());

      if (!overview) {
        return;
      }

      setOverview(overview);
    };

    if (me && isFocused) {
      fetch();
    }
  }, [isFocused, me, selectedStatus, forceRefresh, dispatch]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (_.isNil(selectedStatus) || !_.isNil(prefix)) {
      doSearch({prefix, selectedType, selectedStatus}, setSelected, dispatch);
    }
  }, [isFocused, forceRefresh, prefix, selectedStatus, selectedType]);

  return (
    <View style={sy.mainView}>
      {selectedStatus ? (
        <StatusHeader
          status={selectedStatus}
          count={selected?.filter((item) => item.type === 'request').length}
          onBack={() => navigation.navigate('Search')}
        />
      ) : (
        <View style={[sy['px-4'], sy['pt-4'], sy['pb-3']]}>
          <DealerHeader
            titleStyle={{textTransform: 'capitalize'}}
            title="Opdrachten"
          />

          <View style={[sy['flex-row'], sy['items-center']]}>
            <View
              style={[
                sy['flex-row'],
                sy['flex-1'],
                sy['p-2'],
                sy['mt-2'],
                sy['items-center'],
                {
                  borderRadius: 10,
                  backgroundColor: '#EBEBEB',
                },
              ]}>
              <SearchIcon
                fill={prefix.length ? '#4A4A49' : '#828282'}
                width={18}
                height={18}
              />
              <TextInput
                style={[
                  sy.regular,
                  sy['flex-1'],
                  {
                    color: prefix?.length > 0 ? '#4A4A49' : '#828282',
                    marginLeft: 28,
                  },
                ]}
                placeholder={'Bahnkick'}
                value={prefix}
                onChangeText={setPrefix}
              />
            </View>
            <Animated.View
              style={[
                sy['mt-2'],
                {
                  height: 29,
                  paddingLeft: prefix.length ? 8 : 0,
                  maxWidth: cancelAnim,
                  justifyContent: 'center',
                },
              ]}>
              <Pressable onPress={() => setPrefix('')}>
                <Text
                  style={[
                    sy.regular,
                    {
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    },
                  ]}>
                  Annuleren
                </Text>
              </Pressable>
            </Animated.View>
          </View>
        </View>
      )}
      <Divider style={sy['bg-gray88']} />

      {selected ? (
        <FlatList
          style={[sy['bg-gray95'], sy['px-2']]}
          data={selected}
          renderItem={({item}) => (
            <>
              {item.type === 'month' && <Month date={item.value} />}
              {item.type === 'request' && <Request {...item.value} />}
            </>
          )}
          initialNumToRender={10}
          getItemLayout={(data, index) => {
            let offset = 0;
            for (let i = 0; i < index; i++) {
              const item = data[i];
              if (item.type === 'month') {
                offset += MONTH_HEIGHT;
              } else {
                offset += REQUEST_HEIGHT;
              }
            }

            return {
              length:
                data[index].type === 'month' ? MONTH_HEIGHT : REQUEST_HEIGHT,
              offset,
              index,
            };
          }}
        />
      ) : (
        Object.keys(overview ?? {}).length > 0 && <List overview={overview} />
      )}
    </View>
  );
};

export default withProtectedScreen(Search);
